// Single source of truth for admin role → warehouse binding used by the two
// admin forms (create + edit). Keeping the role dropdown and the abroad_office
// assignment in one place makes it obvious that a "Turkey operator" is scoped
// to abroad_office 9, that a "USA operator" is scoped to 14, etc.
//
// abroad_office_id === null means a global (super) admin — no warehouse scope.
// The backend still stores abroad_office_id independently; the frontend derives
// it from the chosen role at save time (see adminRoleToOfficeId).

const adminRoles = [
  { value: 100, abroadOfficeId: null, tKey: "admin_super_admin" },
  { value: 1,   abroadOfficeId: 9,    tKey: "admin_turkey_operator" },
  { value: 2,   abroadOfficeId: 6,    tKey: "admin_sea_china_operator" },
  { value: 3,   abroadOfficeId: 5,    tKey: "admin_air_china_operator" },
  { value: 4,   abroadOfficeId: 14,   tKey: "admin_usa_operator" },
  { value: 5,   abroadOfficeId: 15,   tKey: "admin_greece_operator" },
  // Georgia operations: the parcel lifecycle pages and OnWay dispatch, nothing
  // else. Global (no warehouse scope) — they handle every warehouse's parcels
  // once those are in Georgia. Enforced server-side by FAdmin as well.
  { value: 200, abroadOfficeId: null, tKey: "admin_ops_operator" },
  // SpaceCargo viewer: the SpaceCargo page, read-only — no pull, no retry,
  // nothing else. Enforced server-side by FAdmin as well.
  { value: 300, abroadOfficeId: null, tKey: "admin_spacecargo_viewer" },
];

// Build the option list for SelectInputValueId. Pass i18n `t` from a component.
export const buildAdminRoleOptions = (t) =>
  adminRoles.map((r) => ({ name: t(r.tKey), value: r.value }));

// Look up the abroad_office_id for a given role, or null if unknown/global.
export const adminRoleToOfficeId = (role) => {
  const r = adminRoles.find((x) => String(x.value) === String(role));
  return r ? r.abroadOfficeId : null;
};

// true if the role is a warehouse operator (as opposed to super admin / unknown).
// Currently: Turkey, Sea-China, Air-China, USA, Greece operators.
export const isWarehouseOperator = (role) =>
  ["1", "2", "3", "4", "5"].includes(String(role));

// true for the top-level super admin (role 100). Falsy for operators / unknown.
export const isSuperAdmin = (role) => String(role) === "100";

// true for the Georgia operations role (200): parcels + OnWay pages only.
export const isOpsOperator = (role) => String(role) === "200";

// true for the SpaceCargo viewer (300): the SpaceCargo page, read-only.
export const isSpaceCargoViewer = (role) => String(role) === "300";

/**
 * Where to send an admin right after login (or when they hit /admin bare).
 * Super admins land on the user list; warehouse operators land on their
 * in-warehouse packages page — anything else falls back to the login screen.
 */
export const roleLandingPath = (role) => {
  if (isSuperAdmin(role)) return "/admin/users";
  if (isWarehouseOperator(role) || isOpsOperator(role)) return "/admin/in-warehouse";
  if (isSpaceCargoViewer(role)) return "/admin/spacecargo";
  return "/admin/login";
};

export default adminRoles;
